import { useFormat } from '@ideavivat/vivat-2';
import useDelivery from '@/useDelivery';
import type { Delivery } from '@/useDelivery';
import type { ID } from '@/types';
import type { Vehicle } from '@/useVehicle';

const { english: shipmentStatus } = useDelivery();

const NOW = useFormat.date().computerShortDate();

export type DELIVERY_NOTE_TYPE = undefined | 'LEASED' | 'NORMAL';
export type DeliveryNoteStatus = 'PREPARE_TO_DELIVER' | 'ON_THE_WAY' | 'NOT_SUCCESS' | 'SOME_SUCCESS' | 'SUCCESS';
export interface Driver {
  id: ID;
  name: string;
  avatarUrl: string;
  lastSevenDay?: string[];
}

export interface DeliveryNote {
  id: ID;
  vehicle?: Vehicle | undefined;
  deliveries: any[];
  status: DeliveryNoteStatus;
  driver?: Driver | undefined;
  deliveryManCount: number;
  uniqueId: string;
  tempDeliveriesId?: number[];
  saved: boolean;
  editable?: boolean;
  deliveryDate?: string;
  type?: DELIVERY_NOTE_TYPE;
}
export const BASE_VEHICLE: Vehicle = {
  id: -1,
  license: '',
  licenseProvince: '',
  model: 'โปรดเลือกรถขนส่ง',
  color: '',
  year: '2077',
  fuelType: 'ElECTRIC',
  mile: '0',
  maxWidth: '0',
  maxLength: '0',
  maxHeight: '0',
  minWeight: 0,
  maxWeight: 0,
  avatarUrl: '',
  driver: {
    id: -1, name: 'โปรดเลือกคนขับรถ', avatarUrl: '', lastSevenDay: [],
  },
  vehicleWeight: 0,
  legalWeight: 0,
};

export const BASE_DRIVER: Driver = {
  id: -1,
  name: '',
  avatarUrl: '',
  lastSevenDay: [],
};

export const BASE_DELIVERY_NOTE: DeliveryNote = {
  id: -1,
  driver: undefined,
  vehicle: undefined,
  deliveryManCount: 0,
  deliveries: [],
  status: 'PREPARE_TO_DELIVER',
  uniqueId: '',
  saved: false,
  editable: false,
  deliveryDate: NOW,
};

export default function useDeliveryNote() {
  const english = [
    'PREPARE_TO_DELIVER',
    'ON_THE_WAY',
    'NOT_SUCCESS',
    'SOME_SUCCESS',
    'SUCCESS',
    // 'HAS_CANCELED_ORDER',
  ];

  const thai = [
    'เตรียมส่งสินค้า',
    'อยู่ระหว่างการส่ง',
    'ส่งสินค้าไม่สำเร็จ',
    'ส่งสำเร็จบางรายการ',
    'ส่งสินค้าสำเร็จ',
    // 'มีรายการถูกยกเลิก',
  ];

  const translate = (status: string) => thai[english.indexOf(status)];

  const deliveryNoteStatusClassMap = (status: string) => {
    switch (status) {
      case 'PREPARE_TO_DELIVER':
        return 'block text-white rounded-xl text-xs py-1 text-center bg-orange-500';
      case 'ON_THE_WAY':
        return 'block text-white rounded-xl text-xs py-1 text-center bg-primary-600';
      case 'SUCCESS':
        return 'block text-white rounded-xl text-xs py-1 text-center bg-green-500';
      case 'SOME_SUCCESS':
        return 'block text-white rounded-xl text-xs py-1 text-center bg-yellow-500';
      case 'NOT_SUCCESS':
        return 'block text-white rounded-xl text-xs py-1 text-center bg-red-500';
      case 'HAS_CANCELED_ORDER':
        return 'block text-gray-600 rounded-xl text-xs py-1 text-center bg-gray-300';
      default: return '';
    }
  };

  function deliveriesStatusToDeliveryNoteStatus(deliveries: Delivery[]) {
    const deliveriesStatus: number[] = [];
    // eslint-disable-next-line
    deliveries?.forEach((delivery: Delivery) => {
      deliveriesStatus.push(shipmentStatus.indexOf(delivery.status as string));
    });
    const minStep = Math.min(...deliveriesStatus);
    const maxStep = Math.max(...deliveriesStatus);
    if (maxStep > 5 /* SOME_SUCCESS */ && minStep < 5 /* NOT_SUCCESS */) return english[3]; // SOME_SUCCESS
    if (minStep === 3 /* ON_THE_WAY */) return english[1]; //  ON_THE_WAY
    if (minStep === 4 /* NOT_SUCCESS */ || minStep === 5 /* SOME_SUCCESS */) return english[2]; // NOT_SUCCESS
    if (minStep === 6 /* SUCCESS */ || minStep === 7 /* HUB */) return english[4]; // SUCCESS
    return english[0];
  }

  return {
    english,
    thai,
    translate,
    deliveryNoteStatusClassMap,
    deliveriesStatusToDeliveryNoteStatus,
  };
}
